import { UserCircleIcon } from '@heroicons/react/24/outline'
import classNames from 'classnames'
import { Copy as CopyIcon, Headset, Phone } from 'iconoir-react'
import { startCase } from 'lodash'
import { ReactElement, ReactNode, useMemo, useState } from 'react'
import { CallEndedByDivider } from 'app/components/activities/CallEndedByDivider'
import { CALL_ENDED_BY_LABEL, callEndedBy } from 'app/components/activities/callEndedBy'
import { KeyValueDrawer } from 'app/components/activities/KeyValueDrawer'
import Button from 'app/components/Button'
import Drawer from 'app/components/drawer/Drawer'
import { CopyToClipboard } from 'app/components/shared/CopyToClipboard'
import Spinner from 'app/components/Spinner'
import { Badge } from 'app/components/ui/badge'
import { TableEmptyState } from 'app/components/ui/table-empty-state'
import { formatDate } from 'app/date.utils'
import { formatPhoneNumber } from 'app/formatPhoneNumber'
import type { CallData } from 'app/generated/graphql'
import { copyToClipboard } from 'app/hooks/useCopyToClipboard'
import {
  type TranscriptToolCall,
  type TranscriptToolResult,
  classifyCallFailures,
  readAnalysisResults,
  readCallOverview,
  readDynamicVariables,
  readNoBookingReason,
  readToolDiagnostics,
  readTranscript,
} from './callDataDebug'
import {
  CALL_FAILURE_GROUP,
  CALL_FAILURE_GROUP_COLOR,
  CALL_FAILURE_GROUP_LABEL,
  CALL_FAILURE_LABEL,
  TOOL_FLAG_COLOR,
  TOOL_FLAG_LABEL,
  noBookingReasonLabel,
} from './voiceAgentOpsMetrics'
import type { OpsCall } from './voiceAgentOps.types'

type DetailTab = `Transcript` | `Debug` | `Raw Data`
const DETAIL_TABS: DetailTab[] = [`Transcript`, `Debug`, `Raw Data`]

// Super-user action styling, matching the call card's Archive button.
const SUPER_ACTION_CLASS = `h-6 !border-amber-500/20 !bg-amber-500/20 !px-3 !text-ui-xs !text-amber-800`

/** "2m 5s" for a whole-call duration. */
function formatDuration(seconds?: number | null): string | undefined {
  if (seconds === undefined || seconds === null) return undefined
  return `${Math.floor(seconds / 60)}m ${Math.floor(seconds % 60)}s`
}

/** "1:05" for a position in the call, as the call card's transcript shows it. */
function formatOffset(seconds: number): string {
  return `${Math.floor(seconds / 60)}:${String(Math.floor(seconds % 60)).padStart(2, `0`)}`
}

/** Pretty-printed JSON in a dark scrollable block with its own copy button, as on the SMS debug trace. */
function JsonBlock({ value }: { value: unknown }): ReactElement {
  const text = typeof value === `string` ? value : JSON.stringify(value, null, 2)
  return (
    <div className="relative">
      <button
        type="button"
        aria-label="Copy to clipboard"
        onClick={() => copyToClipboard(text)}
        className="absolute right-1.5 top-1.5 z-10 flex h-6 w-6 items-center justify-center rounded bg-inverse/80 text-inverse/80 transition-colors hover:bg-inverse hover:text-inverse"
      >
        <CopyIcon className="h-3.5 w-3.5" />
      </button>
      <pre className="max-h-64 overflow-auto whitespace-pre-wrap break-words rounded bg-inverse px-2 py-1.5 pr-9 text-label-xs leading-relaxed text-inverse">
        {text}
      </pre>
    </div>
  )
}

function ToolCallBlock({ call }: { call: TranscriptToolCall }): ReactElement {
  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center gap-1 text-label-xs font-semibold text-blue-700">
        <span aria-hidden>→</span>
        <span>{call.toolName}</span>
        <span className="font-normal text-minimal">call</span>
      </div>
      {call.params !== undefined && <JsonBlock value={call.params} />}
    </div>
  )
}

function ToolResultBlock({ result }: { result: TranscriptToolResult }): ReactElement {
  return (
    <div className="flex flex-col gap-1">
      <div
        className={classNames(
          `flex items-center gap-1 text-label-xs font-semibold`,
          result.isError ? `text-red-600` : `text-emerald-700`,
        )}
      >
        <span aria-hidden>←</span>
        <span>{result.toolName}</span>
        <span className="font-normal text-minimal">{result.isError ? `error` : `result`}</span>
      </div>
      {result.result !== undefined && <JsonBlock value={result.result} />}
    </div>
  )
}

function DetailSection({ title, children }: { title: string; children: ReactNode }): ReactElement {
  return (
    <section className="flex flex-col gap-2">
      <p className="text-heading-xs text-strong">{title}</p>
      {children}
    </section>
  )
}

function NoCallData(): ReactElement {
  return (
    <div className="px-4 py-10">
      <TableEmptyState
        icon={<Phone className="h-6 w-6" strokeWidth={1.5} />}
        title="No ElevenLabs data"
        description="There is no post-call data from ElevenLabs for this call. Preview rows, and calls whose webhook never arrived, have none."
      />
    </div>
  )
}

type CallDetailProps = {
  call: OpsCall
  callData?: CallData | null
  isLoading: boolean
  locationName?: string
  onOpenActivity?: () => void
}

function CallDetail({ call, callData, isLoading, locationName, onOpenActivity }: CallDetailProps): ReactElement {
  const [tab, setTab] = useState<DetailTab>(`Transcript`)
  const data = callData?.data
  const transcript = useMemo(() => readTranscript(data), [data])
  const overview = useMemo(() => readCallOverview(data), [data])
  const diagnostics = useMemo(() => readToolDiagnostics(data), [data])
  const callFailures = useMemo(() => classifyCallFailures(overview), [overview])
  const variables = useMemo(() => readDynamicVariables(data), [data])
  const analysisResults = useMemo(() => readAnalysisResults(data), [data])

  const callerName = call.nameFromActivity || call.contactName || call.contacts?.[0]?.name
  const endedBy = callEndedBy(overview.terminationReason)
  const conversationId = overview.conversationId || callData?.elevenLabsConversationId || undefined
  const cost = callData?.elevenLabsCost?.total
  // The real payload's reason wins; the list's value covers calls whose data hasn't loaded or doesn't exist.
  const noBookingReason = readNoBookingReason(data) ?? call.noBookingReason ?? undefined

  return (
    <div className="flex min-h-0 w-full flex-1 flex-col">
      <div className="flex flex-col gap-4 px-4 pt-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex min-w-0 flex-col gap-1">
            <div className="flex flex-wrap items-center gap-1">
              {call.toolFlags.map((flag) => (
                <Badge key={flag} label={TOOL_FLAG_LABEL[flag]} color={TOOL_FLAG_COLOR[flag]} />
              ))}
              {call.failureTypes.map((type) => (
                <Badge
                  key={type}
                  label={CALL_FAILURE_LABEL[type]}
                  color={CALL_FAILURE_GROUP_COLOR[CALL_FAILURE_GROUP[type]]}
                />
              ))}
            </div>
            <p className="phi truncate text-heading-sm capitalize text-strong">
              {callerName?.toLowerCase() || formatPhoneNumber(call.phoneFromActivity ?? undefined) || `Unknown caller`}
            </p>
            <p className="text-body-xs text-subtle">
              {[formatDate({ value: call.activityDate }), locationName].filter(Boolean).join(` · `)}
            </p>
            {noBookingReason && (
              <p className="text-body-xs text-subtle">
                No booking reason:{' '}
                <span className="text-ui-xs text-strong">{noBookingReasonLabel(noBookingReason)}</span>
              </p>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button
              label="Copy Debug Info"
              mode="white"
              size="sm"
              type="button"
              className={SUPER_ACTION_CLASS}
              disabled={!callData}
              onClick={() => callData && copyToClipboard(JSON.stringify(callData, null, 2), `Debug info copied`)}
            />
            {onOpenActivity && (
              <Button
                label="Open Activity"
                mode="white"
                size="sm"
                type="button"
                className="!text-trn-black h-6 !px-3 !text-ui-xs"
                onClick={onOpenActivity}
              />
            )}
          </div>
        </div>
        <KeyValueDrawer
          map={{
            'Phone Number': formatPhoneNumber(call.phoneFromActivity ?? undefined) || `-`,
            Duration: formatDuration(callData?.duration ?? overview.callDurationSecs) || `-`,
            'Ended By': (endedBy && CALL_ENDED_BY_LABEL[endedBy]) || overview.terminationReason || `-`,
            'Conversation ID': conversationId || `-`,
            'Call ID': call.typeId || `-`,
            'ElevenLabs Cost': cost === undefined || cost === null ? `-` : `$${cost.toFixed(2)}`,
          }}
          className={{ 'Phone Number': `phi` }}
          actions={{
            ...(conversationId && { 'Conversation ID': <CopyToClipboard text={conversationId} /> }),
            ...(call.typeId && { 'Call ID': <CopyToClipboard text={call.typeId} /> }),
          }}
        />
      </div>

      <div className="mt-4 flex gap-x-6 border-y border-default px-4 pt-3" aria-label="Tabs">
        {DETAIL_TABS.map((option) => (
          <button
            key={option}
            type="button"
            className={classNames(
              option === tab ? `text-trn-black border-blue-500` : `border-transparent text-subtle`,
              `flex items-center gap-x-2 whitespace-nowrap border-b-[2px] pb-2.5 text-ui-xs`,
            )}
            onClick={() => setTab(option)}
          >
            {option}
          </button>
        ))}
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto">
        {isLoading ? (
          <div className="flex h-64 items-center justify-center">
            <Spinner size={20} />
          </div>
        ) : tab === `Raw Data` ? (
          callData ? (
            <div className="p-4">
              <pre className="overflow-auto whitespace-pre-wrap break-words rounded border border-default bg-surface-subtle p-3 text-[11px] leading-4 text-strong">
                {JSON.stringify(callData, null, 2)}
              </pre>
            </div>
          ) : (
            <NoCallData />
          )
        ) : !data ? (
          <NoCallData />
        ) : tab === `Transcript` ? (
          transcript.length ? (
            <div className="flex flex-col py-2">
              {transcript.map((entry, index) => {
                const isAgent = entry.role === `agent`
                return (
                  <div key={index} className="flex px-4 py-2">
                    {isAgent ? (
                      <Headset className="h-6 w-6 shrink-0 text-blue-600" strokeWidth={1.5} />
                    ) : (
                      <UserCircleIcon className="h-6 w-6 shrink-0 text-subtle" />
                    )}
                    <div className="ml-3 flex min-w-0 flex-1 flex-col">
                      <div className="flex items-center">
                        <p className="text-heading-xs leading-5 text-strong">
                          {isAgent ? `Agent` : entry.role === `user` ? `Caller` : startCase(entry.role)}
                        </p>
                        {entry.timeInCallSecs !== undefined && (
                          <p className="ml-2 text-caption-md text-subtle">{formatOffset(entry.timeInCallSecs)}</p>
                        )}
                      </div>
                      {entry.message && <p className="phi mt-2 text-body-sm text-strong">{entry.message}</p>}
                      {(entry.toolCalls.length > 0 || entry.toolResults.length > 0) && (
                        <div className="mt-2 flex flex-col gap-2">
                          {entry.toolCalls.map((toolCall, i) => (
                            <ToolCallBlock key={`call-${i}`} call={toolCall} />
                          ))}
                          {entry.toolResults.map((toolResult, i) => (
                            <ToolResultBlock key={`result-${i}`} result={toolResult} />
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )
              })}
              <CallEndedByDivider terminationReason={overview.terminationReason} className="my-1.5" />
            </div>
          ) : (
            <p className="px-4 py-10 text-center text-body-sm text-subtle">This call has no transcript.</p>
          )
        ) : (
          <div className="flex flex-col gap-6 p-4">
            <DetailSection title="Tool Diagnostics">
              {diagnostics.length ? (
                diagnostics.map((diagnostic) => (
                  <div key={diagnostic.flag} className="flex flex-col gap-1 rounded-lg border border-default p-3">
                    <div className="flex items-center gap-2">
                      <Badge label={TOOL_FLAG_LABEL[diagnostic.flag]} color={TOOL_FLAG_COLOR[diagnostic.flag]} />
                      <span className="text-ui-xs text-strong">{String(diagnostic.value)}</span>
                    </div>
                    {diagnostic.rationale && <p className="text-body-sm text-default">{diagnostic.rationale}</p>}
                  </div>
                ))
              ) : (
                <p className="text-body-sm text-subtle">
                  ElevenLabs reported no tool_error, tool_timeout or tool_duplicate result on this call.
                </p>
              )}
            </DetailSection>

            <DetailSection title="ElevenLabs & GPT Failures">
              {callFailures.length ? (
                <div className="flex flex-wrap items-center gap-1">
                  {callFailures.map((type) => (
                    <Badge
                      key={type}
                      label={`${CALL_FAILURE_GROUP_LABEL[CALL_FAILURE_GROUP[type]]} · ${CALL_FAILURE_LABEL[type]}`}
                      color={CALL_FAILURE_GROUP_COLOR[CALL_FAILURE_GROUP[type]]}
                    />
                  ))}
                </div>
              ) : (
                <p className="text-body-sm text-subtle">
                  No LLM failure, dropped connection, max duration, error or unsuccessful verdict on this call.
                </p>
              )}
            </DetailSection>

            <DetailSection title="Call">
              <KeyValueDrawer
                map={{
                  'Termination Reason': overview.terminationReason,
                  'Call Successful': overview.callSuccessful,
                  'Error Code': overview.errorCode,
                  'Error Reason': overview.errorReason,
                  'Summary Title': overview.summaryTitle,
                }}
              />
              {overview.summary && <p className="phi text-body-sm text-strong">{overview.summary}</p>}
            </DetailSection>

            <DetailSection title="Context Passed to ElevenLabs">
              {variables.length ? (
                <div className="flex flex-col divide-y divide-subtle rounded-lg border border-default">
                  {variables.map(([key, value]) => (
                    <div
                      key={key}
                      className="grid grid-cols-1 gap-1 px-3 py-2 md:grid-cols-[12rem_minmax(0,1fr)] md:gap-3"
                    >
                      <span className="break-all font-mono text-label-xs text-subtle">{key}</span>
                      {value !== null && typeof value === `object` ? (
                        <JsonBlock value={value} />
                      ) : (
                        <span className="break-words text-ui-xs text-strong">{String(value)}</span>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-body-sm text-subtle">No dynamic variables came back with this call.</p>
              )}
            </DetailSection>

            {analysisResults.length > 0 && (
              <DetailSection title="Evaluation & Data Collection">
                <div className="flex flex-col divide-y divide-subtle rounded-lg border border-default">
                  {analysisResults.map((result) => (
                    <div key={`${result.kind}-${result.id}`} className="flex flex-col gap-1 px-3 py-2">
                      <div className="flex flex-wrap items-center gap-2">
                        <Badge label={result.kind} color="gray" />
                        <span className="font-mono text-label-xs text-subtle">{result.id}</span>
                        <span className="text-ui-xs text-strong">{String(result.value)}</span>
                      </div>
                      {result.rationale && <p className="text-body-sm text-default">{result.rationale}</p>}
                    </div>
                  ))}
                </div>
              </DetailSection>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

/**
 * AI Call Details, shared by every Ops call list: transcript with tool calls and results
 * inline, the debug context ElevenLabs had (tool diagnostics, ElevenLabs & GPT failures,
 * dynamic variables, analysis, metadata), the raw CallData record, and a Copy Debug Info
 * action that copies all of it.
 */
export function AiCallDetailsDrawer({
  call,
  onClose,
  ...detailProps
}: Omit<CallDetailProps, `call`> & { call?: OpsCall; onClose: () => void }): ReactElement {
  return (
    <Drawer
      open={!!call}
      title="AI Call Details"
      canExpand={false}
      onClose={onClose}
      leftContent={
        <div className="flex w-full flex-col md:w-[680px]">
          {/* Keyed by call so the selected tab resets when another call opens. */}
          {call && <CallDetail key={call.id} call={call} {...detailProps} />}
        </div>
      }
    />
  )
}
